import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import type { MachineFormValues } from "@/features/machines/schema";
import type {
  Machine,
  MachineListRow,
  MachineMaintenanceLog,
  MaintenanceKind,
  MaintenanceStatus,
  ProcessType,
} from "@/features/machines/types";

const text = (value: string | undefined) => value?.trim() || null;

function numOrNull(value: number | undefined) {
  if (value === undefined || value === null || Number.isNaN(value)) return null;
  return value;
}

export function useProcessTypes() {
  return useQuery({
    queryKey: ["process_types"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ProcessType[]> => {
      const { data, error } = await supabase
        .from("process_types")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ProcessType[];
    },
  });
}

export function useMachines() {
  return useQuery({
    queryKey: ["machines"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<MachineListRow[]> => {
      const { data, error } = await supabase
        .from("machines")
        .select(
          "*, process_type:process_types(id, code, name), location:stock_locations(id, code, name)",
        )
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MachineListRow[];
    },
  });
}

export function useUpsertMachine() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: MachineFormValues }) => {
      const payload = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        process_type_id: values.process_type_id,
        machine_type: values.machine_type,
        capacity: text(values.capacity),
        location_id: values.location_id?.trim() || null,
        status: values.status,
        min_thickness_mm: numOrNull(values.min_thickness_mm),
        max_thickness_mm: numOrNull(values.max_thickness_mm),
        max_width_mm: numOrNull(values.max_width_mm),
        description: text(values.description),
        sort_order: values.sort_order,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      if (id) {
        const { data, error } = await supabase
          .from("machines")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as Machine;
      }

      const { data, error } = await supabase
        .from("machines")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data as Machine;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["machines"] });
      toast.success("Machine saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save machine"),
  });
}

export function useDeleteMachine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("machines").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["machines"] });
      toast.success("Machine deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete machine"),
  });
}

export function useMachineMaintenanceLogs(machineId?: string) {
  return useQuery({
    queryKey: ["machine_maintenance_logs", machineId],
    enabled: isSupabaseConfigured && Boolean(machineId),
    queryFn: async (): Promise<MachineMaintenanceLog[]> => {
      const { data, error } = await supabase
        .from("machine_maintenance_logs")
        .select("*")
        .eq("machine_id", machineId!)
        .order("started_at", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as MachineMaintenanceLog[];
    },
  });
}

export function useCreateMaintenanceLog() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      machine_id: string;
      kind: MaintenanceKind;
      started_at: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase
        .from("machine_maintenance_logs")
        .insert({
          machine_id: input.machine_id,
          kind: input.kind,
          status: "open" satisfies MaintenanceStatus,
          started_at: input.started_at,
          notes: input.notes?.trim() || null,
          created_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as MachineMaintenanceLog;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({
        queryKey: ["machine_maintenance_logs", vars.machine_id],
      });
      toast.success("Maintenance logged");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to create log"),
  });
}

export function useCompleteMaintenanceLog() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (row: MachineMaintenanceLog) => {
      const { error } = await supabase
        .from("machine_maintenance_logs")
        .update({
          status: "completed",
          completed_at: new Date().toISOString().slice(0, 10),
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: async (_d, row) => {
      await queryClient.invalidateQueries({
        queryKey: ["machine_maintenance_logs", row.machine_id],
      });
      toast.success("Marked complete");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to complete"),
  });
}
